import type { CompanyRepository } from '@mk1/domain';
import {
  companySnapshotSchema,
  domainEventSchema,
  type CompanySnapshot,
  type DomainEvent,
} from '@mk1/protocol';

const rawSeed = {
  schemaVersion: 1,
  project: {
    name: 'KI-Firma',
    asOf: '2026-08-09',
    day: 11,
    gate: 'G2',
    status: 'IN_PROGRESS_HUMAN_CHANNEL_ACTION_REQUIRED',
    approvedBudgetEur: 30,
    actualSpendEur: 0,
    activeCandidates: 3,
    humanMinutesToday: 18,
  },
  people: [
    {
      id: 'HUM-001',
      name: 'Marvin Kamp',
      nature: 'HUMAN',
      role: 'Founder & CEO',
      managerId: null,
      departmentId: 'DEPT-HUMAN-GATE',
      status: 'WAITING_HUMAN',
      statusLabel: '2 Entscheidungen offen',
      competency: 'Legal and ultimate decision authority',
      levelCeiling: 'L4',
      accessLevels: ['A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6'],
      currentJobId: 'HUM-OPS-CHANNEL-001',
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'HUM-002',
      name: 'Elissa Kamp',
      nature: 'HUMAN',
      role: 'Human Operations Assistant',
      managerId: 'HUM-001',
      departmentId: 'DEPT-HUMAN-GATE',
      status: 'AVAILABLE',
      statusLabel: 'Gebundener Aufgabenbereich',
      competency: 'Human operations in approved scope',
      levelCeiling: 'L3',
      accessLevels: ['A0', 'A1', 'A2', 'A3', 'A4'],
      currentJobId: null,
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'AIR-001',
      name: 'Helena Falk',
      nature: 'PERMANENT_AI',
      role: 'AI COO / Chief of Staff',
      managerId: 'HUM-001',
      departmentId: 'DEPT-EXEC',
      status: 'WORKING',
      statusLabel: 'Control Plane koordinieren',
      competency: 'Governance & orchestration',
      levelCeiling: 'L3',
      accessLevels: ['A0', 'A1', 'A2'],
      currentJobId: 'D11-REAL-VALIDATION-001',
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'AIR-002',
      name: 'Mira Stein',
      nature: 'PERMANENT_AI',
      role: 'Head of AiR',
      managerId: 'AIR-001',
      departmentId: 'DEPT-AIR',
      status: 'AVAILABLE',
      statusLabel: 'Workforce review bereit',
      competency: 'Workforce operations',
      levelCeiling: 'L2',
      accessLevels: ['A0', 'A1', 'A2'],
      currentJobId: null,
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'AIR-003',
      name: 'Viktor Brandt',
      nature: 'PERMANENT_AI',
      role: 'CFO, Risk & Company Administration',
      managerId: 'AIR-001',
      departmentId: 'DEPT-FINANCE',
      status: 'QA_REVIEW',
      statusLabel: 'Budgetkontrolle prüfen',
      competency: 'Finance, risk & admin control',
      levelCeiling: 'L3',
      accessLevels: ['A0', 'A1', 'A2'],
      currentJobId: 'D11-COMPLIANCE-CONTROL-001',
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'AIR-004',
      name: 'Nora Weber',
      nature: 'PERMANENT_AI',
      role: 'Market Intelligence & Growth Experiments',
      managerId: 'AIR-001',
      departmentId: 'DEPT-MARKET',
      status: 'WAITING_HUMAN',
      statusLabel: 'Kanalwahl durch Marvin',
      competency: 'Research & experiment design',
      levelCeiling: 'L3',
      accessLevels: ['A0', 'A1', 'A2'],
      currentJobId: 'EXP-001-OPPORTUNITY-PIPELINE',
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'AIR-005',
      name: 'David Kern',
      nature: 'PERMANENT_AI',
      role: 'Automation & Delivery Systems',
      managerId: 'AIR-001',
      departmentId: 'DEPT-AUTOMATION',
      status: 'WORKING',
      statusLabel: 'Reliability-Simulation',
      competency: 'Automation, software & data',
      levelCeiling: 'L2',
      accessLevels: ['A0', 'A1', 'A2'],
      currentJobId: 'EXP-002-AUTOMATION-RELIABILITY',
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'AIR-006',
      name: 'Leila Hartmann',
      nature: 'PERMANENT_AI',
      role: 'QA, Evidence & Policy Assurance',
      managerId: 'HUM-001',
      departmentId: 'DEPT-QA',
      status: 'QA_REVIEW',
      statusLabel: 'D11 Evidence prüfen',
      competency: 'Independent QA, policy & evidence',
      levelCeiling: 'L3',
      accessLevels: ['A0', 'A1', 'A2'],
      currentJobId: 'D11-COMPLIANCE-CONTROL-001',
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'AIR-007',
      name: 'Jonas Berg',
      nature: 'PERMANENT_AI',
      role: 'Research & Technical Documentation',
      managerId: 'AIR-004',
      departmentId: 'DEPT-RESEARCH',
      status: 'WORKING',
      statusLabel: 'Docs-Muster überarbeiten',
      competency: 'Research & technical documentation',
      levelCeiling: 'L2',
      accessLevels: ['A0', 'A1', 'A2'],
      currentJobId: 'EXP-002-TECHNICAL-DOCS',
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'AIZ-001',
      name: 'Emil Krüger',
      nature: 'AIZUBI',
      role: 'Automation Operations & QA Trainee',
      managerId: 'AIR-005',
      departmentId: 'DEPT-AUTOMATION',
      status: 'WORKING',
      statusLabel: 'Geführte Sandbox-Aufgabe',
      competency: 'Automation operations — guided',
      levelCeiling: 'L1',
      accessLevels: ['A0', 'A1'],
      currentJobId: 'D12-DELIVERY-SIMULATION-001',
      provenance: 'AUTHORIZED_SEED',
    },
    {
      id: 'AIZ-002',
      name: 'Sofia Neumann',
      nature: 'AIZUBI',
      role: 'Research / Docs / Experiment Ops Trainee',
      managerId: 'AIR-007',
      departmentId: 'DEPT-RESEARCH',
      status: 'WAITING_HUMAN',
      statusLabel: 'Kontaktfreigabe ausstehend',
      competency: 'Research & docs — guided',
      levelCeiling: 'L1',
      accessLevels: ['A0', 'A1'],
      currentJobId: 'EXP-003-ERP-MAPPING',
      provenance: 'AUTHORIZED_SEED',
    },
  ],
  departments: [
    {
      id: 'DEPT-EXEC',
      name: 'Executive Operations',
      shortName: 'Executive Ops',
      leadId: 'AIR-001',
      objective: 'G2-Portfolio sicher durch die Human Gates führen',
      health: 'ATTENTION',
      queueCount: 3,
      oldestItemHours: 6.4,
      nextDeadline: 'Heute · 18:00',
      memberIds: ['AIR-001'],
      tools: ['Project Records', 'Decision Log'],
      throughput: [2, 3, 3, 5, 4, 6, 5],
    },
    {
      id: 'DEPT-AIR',
      name: 'AiR / Workforce',
      shortName: 'AiR Workforce',
      leadId: 'AIR-002',
      objective: 'Load, Training und Zugriffsgrenzen kalibrieren',
      health: 'HEALTHY',
      queueCount: 1,
      oldestItemHours: 2.1,
      nextDeadline: 'Tag 15',
      memberIds: ['AIR-002'],
      tools: ['Workforce Plan', 'Training Ledger'],
      throughput: [1, 2, 2, 2, 3, 2, 3],
    },
    {
      id: 'DEPT-FINANCE',
      name: 'Finance, Risk & Company Administration',
      shortName: 'Finance & Risk',
      leadId: 'AIR-003',
      objective: '€30 Testbudget und Compliance-Kontrollen sichern',
      health: 'HEALTHY',
      queueCount: 2,
      oldestItemHours: 3.2,
      nextDeadline: 'Heute · 17:00',
      memberIds: ['AIR-003'],
      tools: ['Budget Ledger', 'Risk Register'],
      throughput: [1, 1, 2, 2, 2, 3, 3],
    },
    {
      id: 'DEPT-MARKET',
      name: 'Market & Growth Lab',
      shortName: 'Market Lab',
      leadId: 'AIR-004',
      objective: 'Reale G2-Kanäle auswählen und evidenzbasiert testen',
      health: 'BLOCKED',
      queueCount: 4,
      oldestItemHours: 11.5,
      nextDeadline: 'Human Gate heute',
      memberIds: ['AIR-004'],
      tools: ['Opportunity Pipeline', 'Evidence Pack'],
      throughput: [5, 6, 5, 5, 4, 3, 2],
    },
    {
      id: 'DEPT-AUTOMATION',
      name: 'Automation & Delivery',
      shortName: 'Automation',
      leadId: 'AIR-005',
      objective: 'Reliability- und Delivery-Simulation validieren',
      health: 'HEALTHY',
      queueCount: 2,
      oldestItemHours: 1.8,
      nextDeadline: 'Morgen · 12:00',
      memberIds: ['AIR-005', 'AIZ-001'],
      tools: ['Local Demo Runner', 'Test Harness'],
      throughput: [2, 3, 4, 4, 5, 6, 6],
    },
    {
      id: 'DEPT-RESEARCH',
      name: 'Research & Documentation',
      shortName: 'Research & Docs',
      leadId: 'AIR-007',
      objective: 'Technische Evidenz und ERP-Mapping liefern',
      health: 'ATTENTION',
      queueCount: 3,
      oldestItemHours: 7.1,
      nextDeadline: 'Morgen · 10:00',
      memberIds: ['AIR-007', 'AIZ-002'],
      tools: ['Docs Workspace', 'Source Register'],
      throughput: [4, 4, 5, 4, 6, 5, 5],
    },
    {
      id: 'DEPT-QA',
      name: 'Independent QA & Policy',
      shortName: 'QA & Policy',
      leadId: 'AIR-006',
      objective: 'Evidence, Rechte und Release-Grenzen unabhängig prüfen',
      health: 'HEALTHY',
      queueCount: 2,
      oldestItemHours: 2.8,
      nextDeadline: 'Heute · 16:30',
      memberIds: ['AIR-006'],
      tools: ['Evidence Register', 'Policy Matrix'],
      throughput: [2, 2, 3, 3, 4, 4, 5],
    },
    {
      id: 'DEPT-HUMAN-GATE',
      name: 'Human Gate Desk',
      shortName: 'Human Gates',
      leadId: 'HUM-001',
      objective: 'Externe und identitätsgebundene Schritte sicher freigeben',
      health: 'BLOCKED',
      queueCount: 2,
      oldestItemHours: 13.2,
      nextDeadline: 'Jetzt',
      memberIds: ['HUM-001', 'HUM-002'],
      tools: ['Approval Inbox', 'Human Task Queue'],
      throughput: [2, 1, 2, 1, 2, 1, 0],
    },
  ],
  jobs: [
    {
      id: 'HUM-OPS-CHANNEL-001',
      title: 'Fiverr-Onboarding und Proposal-Kanal auswählen',
      family: 'G2-01 Software Readiness Factory',
      departmentId: 'DEPT-HUMAN-GATE',
      ownerId: 'HUM-001',
      status: 'BLOCKED',
      priority: 'P0',
      risk: 'R3',
      dataClass: 'INTERNAL',
      deadline: '2026-08-09T18:00:00.000Z',
      blockedReason: 'Menschliche Kanal- und Identitätsaktion ist noch nicht ausgeführt.',
      nextOwnerId: 'HUM-001',
      qaReviewerId: 'AIR-006',
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'D11-REAL-VALIDATION-001',
      title: 'G2 Real Validation Release koordinieren',
      family: 'G2 Portfolio',
      departmentId: 'DEPT-EXEC',
      ownerId: 'AIR-001',
      status: 'WAITING_HUMAN',
      priority: 'P1',
      risk: 'R2',
      dataClass: 'INTERNAL',
      deadline: '2026-08-09T18:00:00.000Z',
      blockedReason: 'Wartet auf Human Gate für den externen Kanal.',
      nextOwnerId: 'HUM-001',
      qaReviewerId: 'AIR-006',
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'D11-COMPLIANCE-CONTROL-001',
      title: 'Rechte- und Budgetkontrolle für G2',
      family: 'G2 Portfolio',
      departmentId: 'DEPT-QA',
      ownerId: 'AIR-006',
      status: 'QA_REVIEW',
      priority: 'P1',
      risk: 'R2',
      dataClass: 'INTERNAL',
      deadline: '2026-08-09T16:30:00.000Z',
      blockedReason: null,
      nextOwnerId: 'AIR-003',
      qaReviewerId: 'HUM-001',
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'EXP-001-OPPORTUNITY-PIPELINE',
      title: 'Software Readiness Opportunity Pipeline',
      family: 'G2-01 Software Readiness Factory',
      departmentId: 'DEPT-MARKET',
      ownerId: 'AIR-004',
      status: 'WAITING_HUMAN',
      priority: 'P1',
      risk: 'R2',
      dataClass: 'INTERNAL',
      deadline: '2026-08-10T12:00:00.000Z',
      blockedReason: 'Proposal-Kanal muss menschlich ausgewählt werden.',
      nextOwnerId: 'HUM-002',
      qaReviewerId: 'AIR-006',
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'EXP-002-AUTOMATION-RELIABILITY',
      title: 'Automation Reliability Testlauf',
      family: 'G2-02 Automation Reliability',
      departmentId: 'DEPT-AUTOMATION',
      ownerId: 'AIR-005',
      status: 'RUNNING',
      priority: 'P2',
      risk: 'R1',
      dataClass: 'SYNTHETIC',
      deadline: '2026-08-10T12:00:00.000Z',
      blockedReason: null,
      nextOwnerId: 'AIR-006',
      qaReviewerId: 'AIR-006',
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'EXP-002-TECHNICAL-DOCS',
      title: 'Technical Documentation Musterseite',
      family: 'G2-01 Software Readiness Factory',
      departmentId: 'DEPT-RESEARCH',
      ownerId: 'AIR-007',
      status: 'ASSIGNED',
      priority: 'P2',
      risk: 'R1',
      dataClass: 'SYNTHETIC',
      deadline: '2026-08-10T10:00:00.000Z',
      blockedReason: null,
      nextOwnerId: 'AIR-006',
      qaReviewerId: 'AIR-006',
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'EXP-003-ERP-MAPPING',
      title: 'ERP Integration Readiness Kontakt-Mapping',
      family: 'G2-03 ERP Integration Readiness',
      departmentId: 'DEPT-RESEARCH',
      ownerId: 'AIZ-002',
      status: 'WAITING_HUMAN',
      priority: 'P1',
      risk: 'R2',
      dataClass: 'INTERNAL',
      deadline: '2026-08-10T14:00:00.000Z',
      blockedReason: 'Acht Kontakte müssen rechtmäßig bestätigt und ausgewählt werden.',
      nextOwnerId: 'HUM-001',
      qaReviewerId: 'AIR-006',
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'D12-DELIVERY-SIMULATION-001',
      title: 'Geführte Delivery-Simulation',
      family: 'G2-02 Automation Reliability',
      departmentId: 'DEPT-AUTOMATION',
      ownerId: 'AIZ-001',
      status: 'QUEUED',
      priority: 'P3',
      risk: 'R0',
      dataClass: 'SYNTHETIC',
      deadline: '2026-08-11T12:00:00.000Z',
      blockedReason: null,
      nextOwnerId: 'AIR-005',
      qaReviewerId: 'AIR-006',
      provenance: 'SYNTHETIC_DEMO',
    },
  ],
  grants: [
    ['GRANT-0001', 'AIR-001', 'KI-Firma project records', 'internal read/write', 'A2', 'ACTIVE'],
    ['GRANT-0002', 'AIR-002', 'company/air/*', 'internal read/write', 'A2', 'ACTIVE'],
    ['GRANT-0003', 'AIR-003', 'finance/economics records', 'internal read/write', 'A2', 'ACTIVE'],
    ['GRANT-0004', 'AIR-004', 'market/experiment records', 'internal read/write', 'A2', 'ACTIVE'],
    ['GRANT-0005', 'AIR-005', 'technical/delivery drafts', 'internal read/write', 'A2', 'ACTIVE'],
    ['GRANT-0006', 'AIR-006', 'all project records', 'read + QA/evidence write', 'A2', 'ACTIVE'],
    ['GRANT-0007', 'AIR-007', 'research/docs records', 'internal read/write', 'A2', 'ACTIVE'],
    ['GRANT-0008', 'AIZ-001', 'training artifacts', 'sandbox read/write', 'A1', 'ACTIVE'],
    ['GRANT-0008-B', 'AIZ-002', 'training artifacts', 'sandbox read/write', 'A1', 'ACTIVE'],
    [
      'GRANT-0009',
      'HUM-002',
      'human operations queue',
      'bounded human execution',
      'A4',
      'ACTIVE_BOUNDED',
    ],
  ].map(([id, personId, resource, permission, accessLevel, status], index) => ({
    id,
    personId,
    resourceId: `RES-${String(index + 1).padStart(4, '0')}`,
    resource,
    permission,
    accessLevel,
    status,
    expiry: '2026-08-15T18:00:00.000Z',
    provenance: 'AUTHORIZED_SEED',
  })),
  sessions: [
    {
      id: 'SESSION-DEMO-0001',
      personId: 'AIR-005',
      runnerNodeId: 'RUNNER-DEMO-LOCAL',
      providerPoolId: 'POOL-OPENAI-PRO',
      jobId: 'EXP-002-AUTOMATION-RELIABILITY',
      status: 'SIMULATED_ACTIVE',
      tool: 'Fixture CLI — no provider process',
      provenance: 'SYNTHETIC_DEMO',
    },
  ],
  providerPools: [
    {
      id: 'POOL-OPENAI-PRO',
      provider: 'OpenAI',
      plan: 'ChatGPT Pro / Codex',
      tool: 'Codex CLI',
      status: 'AUTH_REQUIRED',
      confidence: 'UNKNOWN',
      remainingLabel: null,
      resetLabel: null,
      policy: 'Guarded owner smoke test required · no API key or spend',
      runnerLabel: 'No runner enrolled',
      adapterKey: 'OPENAI_CODEX',
      enabled: false,
      policyReviewStatus: 'SAFE_TO_SMOKE_TEST',
      policyReviewedAt: '2026-08-10T00:00:00.000Z',
      taskDomains: [
        'SOFTWARE_ENGINEERING',
        'RESEARCH',
        'DOCUMENTATION',
        'OPERATIONS',
        'POLICY_REVIEW',
      ],
      allowedDataClasses: ['PUBLIC', 'INTERNAL', 'SYNTHETIC'],
      capacitySource: 'NO_MEASUREMENT',
      remainingValue: null,
      remainingUnit: 'UNKNOWN',
      resetsAt: null,
      weeklyResetsAt: null,
      noSpend: true,
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'POOL-CLAUDE-MAX',
      provider: 'Anthropic',
      plan: 'Claude Max 5x',
      tool: 'Claude Code',
      status: 'POLICY_BLOCKED',
      confidence: 'UNKNOWN',
      remainingLabel: null,
      resetLabel: null,
      policy: 'Blocked by current consumer terms for automated/non-human access',
      runnerLabel: 'No runner enrolled',
      adapterKey: 'ANTHROPIC_CLAUDE_CODE',
      enabled: false,
      policyReviewStatus: 'BLOCKED_BY_POLICY',
      policyReviewedAt: '2026-08-10T00:00:00.000Z',
      taskDomains: ['SOFTWARE_ENGINEERING', 'DOCUMENTATION', 'POLICY_REVIEW'],
      allowedDataClasses: ['PUBLIC', 'INTERNAL', 'SYNTHETIC'],
      capacitySource: 'NO_MEASUREMENT',
      remainingValue: null,
      remainingUnit: 'UNKNOWN',
      resetsAt: null,
      weeklyResetsAt: null,
      noSpend: true,
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'POOL-ZAI-LITE',
      provider: 'Z.AI',
      plan: 'GLM Coding Plan Lite',
      tool: 'Supported coding tool',
      status: 'AUTH_REQUIRED',
      confidence: 'UNKNOWN',
      remainingLabel: null,
      resetLabel: null,
      policy: 'Coding-only through supported OpenCode · guarded owner smoke test required',
      runnerLabel: 'No runner enrolled',
      adapterKey: 'ZAI_OPENCODE',
      enabled: false,
      policyReviewStatus: 'SAFE_TO_SMOKE_TEST',
      policyReviewedAt: '2026-08-10T00:00:00.000Z',
      taskDomains: ['SOFTWARE_ENGINEERING'],
      allowedDataClasses: ['PUBLIC', 'INTERNAL', 'SYNTHETIC'],
      capacitySource: 'NO_MEASUREMENT',
      remainingValue: null,
      remainingUnit: 'UNKNOWN',
      resetsAt: null,
      weeklyResetsAt: null,
      noSpend: true,
      provenance: 'SYNTHETIC_DEMO',
    },
  ],
  approvals: [
    {
      id: 'GATE-DEMO-001',
      type: 'HG-EXTERNAL-SEND',
      title: 'Proposal-Kanal freigeben',
      action:
        'Ein menschlicher Operator wählt einen rechtskonformen Kanal; kein automatischer Versand.',
      requesterId: 'AIR-004',
      approverId: 'HUM-001',
      affectedPersonId: 'HUM-002',
      consequence:
        'Nach Freigabe darf Elissa genau den dokumentierten manuellen Kanal-Schritt ausführen.',
      evidence: ['DEC-0019 channel boundary', 'G2 evidence pack', 'QA review pending'],
      payloadPreview: 'Kanalwahl + ungesendeter Proposal-Entwurf v1',
      risk: 'R3',
      dataClass: 'INTERNAL',
      expiresLabel: 'Heute · 18:00',
      status: 'PENDING',
      provenance: 'SYNTHETIC_DEMO',
    },
    {
      id: 'GATE-DEMO-002',
      type: 'HG-ACCOUNT',
      title: 'ERP-Kontakte bestätigen',
      action: 'Marvin bestätigt acht rechtmäßig privat/geschäftlich nutzbare Kontakte.',
      requesterId: 'AIZ-002',
      approverId: 'HUM-001',
      affectedPersonId: 'AIR-004',
      consequence:
        'Erst danach darf ein internes, nicht versendetes Kontakt-Mapping erstellt werden.',
      evidence: ['EXP-003 contact worksheet', 'Employer/customer lists excluded'],
      payloadPreview: 'Kontakt-IDs werden erst nach Human-Auswahl ergänzt',
      risk: 'R2',
      dataClass: 'INTERNAL',
      expiresLabel: 'Morgen · 10:00',
      status: 'PENDING',
      provenance: 'SYNTHETIC_DEMO',
    },
  ],
  events: [
    [
      'EVT-DEMO-006',
      'JOB.WAITING_HUMAN',
      '2026-08-09T12:38:00.000Z',
      'AIR-004',
      'EXP-001-OPPORTUNITY-PIPELINE',
      'ATTENTION',
      'Opportunity Pipeline wartet auf Kanalwahl.',
    ],
    [
      'EVT-DEMO-005',
      'JOB.QA_REQUESTED',
      '2026-08-09T12:36:00.000Z',
      'AIR-003',
      'D11-COMPLIANCE-CONTROL-001',
      'INFO',
      'Budget- und Rechteprüfung an Leila übergeben.',
    ],
    [
      'EVT-DEMO-004',
      'SESSION.PROGRESS',
      '2026-08-09T12:34:00.000Z',
      'SYSTEM',
      'SESSION-DEMO-0001',
      'INFO',
      'Deterministischer Reliability-Schritt 2/4 protokolliert.',
    ],
    [
      'EVT-DEMO-003',
      'GATE.REQUESTED',
      '2026-08-09T12:32:00.000Z',
      'AIR-004',
      'GATE-DEMO-001',
      'BLOCKER',
      'Human Gate für Proposal-Kanal angefordert.',
    ],
    [
      'EVT-DEMO-002',
      'JOB.STARTED',
      '2026-08-09T12:30:00.000Z',
      'AIR-005',
      'EXP-002-AUTOMATION-RELIABILITY',
      'INFO',
      'Synthetische Reliability-Simulation gestartet.',
    ],
    [
      'EVT-DEMO-001',
      'COMPANY.SNAPSHOT_LOADED',
      '2026-08-09T12:28:00.000Z',
      'SYSTEM',
      'KI-Firma',
      'INFO',
      'Autorisierter Seed und Demo-State geladen.',
    ],
  ].map(([id, type, occurredAt, actorId, subjectId, severity, message]) => ({
    id,
    type,
    occurredAt,
    actorId,
    subjectId,
    correlationId: 'CORR-DEMO-DAY11',
    severity,
    message,
    provenance: 'SYNTHETIC_DEMO',
    schemaVersion: 1,
  })),
  notifications: [
    {
      id: 'NOTE-001',
      severity: 'BLOCKER',
      title: 'Human Channel blockiert G2',
      detail: 'Onboarding und Kanalwahl sind noch menschlich auszuführen.',
      nextAction: 'Gate-Paket öffnen',
      sourceId: 'HUM-OPS-CHANNEL-001',
    },
    {
      id: 'NOTE-002',
      severity: 'ATTENTION',
      title: 'ERP-Kontakte unbestätigt',
      detail: 'Acht Kontakte benötigen eine rechtmäßige Human-Auswahl.',
      nextAction: 'Kontakt-Gate prüfen',
      sourceId: 'EXP-003-ERP-MAPPING',
    },
    {
      id: 'NOTE-003',
      severity: 'INFO',
      title: 'Provider bleiben getrennt',
      detail: 'Alle Kapazitätskarten sind nicht verbunden und synthetisch.',
      nextAction: 'Capacity Grid ansehen',
      sourceId: 'POOL-OPENAI-PRO',
    },
  ],
  accessControl: {
    schemaVersion: 1,
    policyVersion: 2,
    policies: [
      ['A0', 'public/read-only', 'all roles', false],
      ['A1', 'internal/sandbox draft', 'certified guided work', false],
      ['A2', 'update assigned internal project records', 'L2+ plus explicit grant', false],
      ['A3', 'prepare external/customer draft', 'L3 plus job and rights-safe workspace', false],
      ['A4', 'send/publish/use external account', 'human release and exact one-action gate', true],
      [
        'A5',
        'customer data, secrets, credentials, production',
        'exceptional time-bound human approval',
        true,
      ],
      ['A6', 'contracts, purchases, payments, subscriptions, legal/tax', 'human only', true],
    ].map(([level, meaning, boundary, humanRequired]) => ({
      level,
      meaning,
      boundary,
      humanRequired,
      policyVersion: 2,
    })),
    resources: [
      ...[
        'KI-Firma project records',
        'company/air/*',
        'finance/economics records',
        'market/experiment records',
        'technical/delivery drafts',
        'all project records',
        'research/docs records',
        'training artifacts',
        'training artifacts',
        'human operations queue',
      ].map((label, index) => ({
        id: `RES-${String(index + 1).padStart(4, '0')}`,
        type: 'PROJECT_RECORD',
        label,
        classification: 'INTERNAL',
        ownership: 'OWNER_CLEARED',
        usage: 'INTERNAL_ONLY',
        evidenceRef: `seed/resource/RES-${String(index + 1).padStart(4, '0')}`,
        notes: 'Authorized bounded project-record metadata; no credential material.',
        secretReferenceState: 'NONE',
        metadata: { connectionStatus: 'LOCAL_ONLY' },
        provenance: 'SYNTHETIC_DEMO',
      })),
      {
        id: 'ASSET-VPS-IONOS-01',
        type: 'VPS',
        label: 'IONOS Linux VPS (existing shared host)',
        classification: 'INTERNAL',
        ownership: 'OWNER_CLEARED',
        usage: 'POTENTIAL',
        evidenceRef: 'company/FOUNDER_ASSET_INVENTORY.md',
        notes:
          'Potential backend only after inventory, isolation, backup and secret review; not connected or scanned.',
        secretReferenceState: 'NONE',
        metadata: { provider: 'IONOS', connectionStatus: 'NOT_CONNECTED' },
        provenance: 'AUTHORIZED_SEED',
      },
      {
        id: 'ASSET-DOMAIN-MK1-ROCKS',
        type: 'DOMAIN',
        label: 'mk1.rocks',
        classification: 'PUBLIC',
        ownership: 'OWNER_CONFIRMED',
        usage: 'ACTIVE_EXISTING',
        evidenceRef: 'company/EVIDENCE_REGISTER.md#EV-0010',
        notes: 'PersonaStudio domain; DNS and deployment details were not inspected.',
        secretReferenceState: 'NONE',
        metadata: { product: 'PersonaStudio', dnsStatus: 'NOT_INSPECTED' },
        provenance: 'AUTHORIZED_SEED',
      },
      {
        id: 'ASSET-DOMAIN-ROAMTALE-IO',
        type: 'DOMAIN',
        label: 'roamtale.io',
        classification: 'PUBLIC',
        ownership: 'OWNER_CONFIRMED',
        usage: 'ACTIVE_EXISTING',
        evidenceRef: 'company/EVIDENCE_REGISTER.md#EV-0009',
        notes: 'RoamTale domain; DNS and deployment details were not inspected.',
        secretReferenceState: 'NONE',
        metadata: { product: 'RoamTale', dnsStatus: 'NOT_INSPECTED' },
        provenance: 'AUTHORIZED_SEED',
      },
      {
        id: 'ASSET-MAILBOX-UNKNOWN',
        type: 'MAILBOX',
        label: 'KI-Firma operations mailbox',
        classification: 'INTERNAL',
        ownership: 'UNKNOWN',
        usage: 'UNKNOWN',
        evidenceRef: 'company/PROJECT_STATE.md#critical_unknowns',
        notes: 'No mailbox address, account or permission is assumed or stored.',
        secretReferenceState: 'NONE',
        metadata: { accountStatus: 'NOT_ESTABLISHED' },
        provenance: 'AUTHORIZED_SEED',
      },
      {
        id: 'ASSET-SUBSCRIPTION-OPENAI',
        type: 'SUBSCRIPTION',
        label: 'ChatGPT Pro / Codex capacity',
        classification: 'INTERNAL',
        ownership: 'OWNER_CLEARED',
        usage: 'INTERNAL_ONLY',
        evidenceRef: 'docs/provider-policy-matrix.md',
        notes: 'Modeled capacity only; no provider authentication was performed.',
        secretReferenceState: 'NONE',
        metadata: { status: 'AUTH_REQUIRED' },
        provenance: 'SYNTHETIC_DEMO',
      },
      {
        id: 'ASSET-RUNNER-LOCAL',
        type: 'RUNNER_NODE',
        label: 'Trusted local runner boundary',
        classification: 'INTERNAL',
        ownership: 'OWNER_CLEARED',
        usage: 'INTERNAL_ONLY',
        evidenceRef: 'docs/threat-model-phase-4.md',
        notes: 'Real node enrollment remains unconfigured; deterministic fake nodes are separate.',
        secretReferenceState: 'NONE',
        metadata: { enrollment: 'NOT_CONFIGURED' },
        provenance: 'SYNTHETIC_DEMO',
      },
    ],
    expiringGrantIds: [],
    companyPaused: false,
    drill: {
      id: 'SYNTHETIC-EMERGENCY-DRILL-01',
      status: 'NOT_RUN',
      startedAt: null,
      completedAt: null,
      correlationId: null,
      affectedJobIds: [],
      auditEventIds: [],
      evidencePath: null,
      synthetic: true,
    },
  },
} as const;

export const companySeed: CompanySnapshot = companySnapshotSchema.parse(rawSeed);

export class InMemoryCompanyRepository implements CompanyRepository {
  #snapshot: CompanySnapshot = structuredClone(companySeed);
  #listeners = new Set<(snapshot: CompanySnapshot) => void>();

  async getSnapshot() {
    return structuredClone(this.#snapshot);
  }

  subscribe(listener: (snapshot: CompanySnapshot) => void) {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  revokeDemoTool(personId: string): DomainEvent {
    const session = this.#snapshot.sessions.find((item) => item.personId === personId);
    if (!session) throw new Error(`No synthetic session for ${personId}`);
    if (session.status === 'SIMULATED_REVOKED') return this.#snapshot.events[0]!;

    session.status = 'SIMULATED_REVOKED';
    const event = domainEventSchema.parse({
      id: 'EVT-DEMO-REVOKE-001',
      type: 'TOOL_LEASE.REVOKED',
      occurredAt: '2026-08-09T12:40:00.000Z',
      actorId: 'HUM-001',
      subjectId: session.id,
      correlationId: 'CORR-DEMO-REVOKE-001',
      severity: 'ATTENTION',
      message: 'Demo-Lease widerrufen; keine reale Berechtigung oder Provider-Session verändert.',
      provenance: 'SYNTHETIC_DEMO',
      schemaVersion: 1,
    });
    this.#snapshot.events = [event, ...this.#snapshot.events];
    this.#listeners.forEach((listener) => listener(structuredClone(this.#snapshot)));
    return event;
  }

  revokeTool(personId: string): DomainEvent {
    return this.revokeDemoTool(personId);
  }
}

export const deterministicEventSequence = companySeed.events.toReversed();
